import { api, ApiError } from "@/api/client";

export type GroupTeam = { name: string; flag: string };

let _wmStartCache: { ts: number; value: string | null } | null = null;
let _groupTeamsCache: { ts: number; value: Record<string, GroupTeam[]> } | null = null;
let _playersCache: { ts: number; value: Player[] } | null = null;

export type Player = { id: string; name: string; team: string; position: string | null };

// A "real WM fixture" — i.e. a game that should drive bonus-question
// lock + group-stage logic — has a single-letter groupLabel A..L.
// Imported one-off fixtures use groupLabel "EXT", and test games use
// arbitrary strings (default "TEST"). Filtering on length === 1
// excludes both without needing to look up external_id on the client.
const isRealWMGame = (g: any) =>
  typeof g.groupLabel === "string" && /^[A-L]$/.test(g.groupLabel);

export async function loadGroupTeams(): Promise<Record<string, GroupTeam[]>> {
  if (_groupTeamsCache && Date.now() - _groupTeamsCache.ts < 60_000) return _groupTeamsCache.value;
  const games = await api.games.list({ phase: "group" });
  const map: Record<string, Map<string, string>> = {};
  for (const g of games as any[]) {
    if (!isRealWMGame(g)) continue;
    const k = g.groupLabel;
    if (!k || k === "?") continue;
    map[k] = map[k] ?? new Map();
    map[k].set(g.homeTeam, g.homeFlag);
    map[k].set(g.awayTeam, g.awayFlag);
  }
  const out: Record<string, GroupTeam[]> = {};
  Object.keys(map)
    .sort()
    .forEach((k) => {
      out[k] = Array.from(map[k].entries())
        .map(([name, flag]) => ({ name, flag }))
        .sort((a, b) => a.name.localeCompare(b.name));
    });
  _groupTeamsCache = { ts: Date.now(), value: out };
  return out;
}

export async function loadPlayers(): Promise<Player[]> {
  if (_playersCache && Date.now() - _playersCache.ts < 60_000) return _playersCache.value;
  const list = (await api.players.list()) as Player[];
  _playersCache = { ts: Date.now(), value: list };
  return list;
}

export async function getWMStart(): Promise<string | null> {
  if (_wmStartCache && Date.now() - _wmStartCache.ts < 60_000) return _wmStartCache.value;
  const games = (await api.games.list({ phase: "group" })) as any[];
  // Skip imported / test games — they share `phase: "group"` for storage
  // reasons but aren't part of the WM. Otherwise importing e.g. an
  // Arsenal vs Burnley test fixture would flip the bonus lock the second
  // its kickoff is in the past.
  const earliest = games
    .filter(isRealWMGame)
    .map((g) => g.kickoffTime as string)
    .sort()
    .at(0) ?? null;
  _wmStartCache = { ts: Date.now(), value: earliest };
  return earliest;
}

export function isBonusLocked(wmStart: string | null): boolean {
  if (!wmStart) return false;
  return Date.now() >= new Date(wmStart).getTime();
}

// Max bonus points:
//   20  Weltmeister
//   15  Torschützenkönig
//   60  12 Gruppensieger × 5
//   30  6 Österreich-Fragen × 5
//   ---
//  125
export const BONUS_MAX_POINTS = 125;

// Static labels for the AT progression dropdown. Kept here (not the
// component) so Bonus + Admin + future stats stay in sync.
export const AT_PROGRESSION_OPTIONS: { value: import("@/api/client").AtProgression; label: string }[] = [
  { value: "group",        label: "Gruppenphase" },
  { value: "r32",          label: "Sechzehntelfinale" },
  { value: "r16",          label: "Achtelfinale" },
  { value: "qf",           label: "Viertelfinale" },
  { value: "sf",           label: "Halbfinale" },
  { value: "final_loser",  label: "Finale (verloren)" },
  { value: "winner",       label: "Weltmeister" },
];

export async function checkUsernameAvailable(username: string): Promise<boolean> {
  try {
    const r = await api.profiles.usernameAvailable(username);
    return r.available;
  } catch (err) {
    // Fail open — final upsert will catch the conflict
    if (err instanceof ApiError) return true;
    return true;
  }
}

export async function checkGroupNameAvailable(name: string): Promise<boolean> {
  try {
    const r = await api.groups.nameAvailable(name);
    return r.available;
  } catch {
    return true;
  }
}
